import React, { useState, useEffect } from 'react';
import apiService from '../../../../services/shared/apiService';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface RadarTrendGraphProps {
  patientId: string;
}

// Mock data until backend endpoint is ready
const mockRadarScores = [
    { week: '2024-05-01', scores: { sleep: 4, energy: 3, mood: 5, social: 2, focus: 4 } },
    { week: '2024-05-08', scores: { sleep: 3, energy: 4, mood: 4, social: 3, focus: 3 } },
    { week: '2024-05-15', scores: { sleep: 5, energy: 5, mood: 5, social: 4, focus: 5 } },
    { week: '2024-05-22', scores: { sleep: 4, energy: 3, mood: 4, social: 3, focus: 4 } },
];

const MOCK_AXES = ['sleep', 'energy', 'mood', 'social', 'focus'];
const MOCK_COLORS = {
    sleep: 'rgba(75, 192, 192, 1)',
    energy: 'rgba(255, 206, 86, 1)',
    mood: 'rgba(54, 162, 235, 1)',
    social: 'rgba(255, 99, 132, 1)',
    focus: 'rgba(153, 102, 255, 1)',
};

const RadarTrendGraph: React.FC<RadarTrendGraphProps> = ({ patientId }) => {
    const [scores, setScores] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [visibleAxes, setVisibleAxes] = useState<Record<string, boolean>>(
        MOCK_AXES.reduce((acc, axis) => ({...acc, [axis]: true }), {})
    );

    useEffect(() => {
        const fetchScores = async () => {
            try {
                // const data = await apiService.getWeeklyRadarScores(patientId);
                // setScores(data);
                // Using mock data for now
                setScores(mockRadarScores);
            } catch (err) {
                console.error("Using mock data due to error:", err);
                // setError('Failed to load radar scores. Displaying sample data.');
                setScores(mockRadarScores); // Fallback to mock data on error
            } finally {
                setIsLoading(false);
            }
        };
        fetchScores();
    }, [patientId]);
    
    const handleToggleAxis = (axis: string) => {
        setVisibleAxes(prev => ({...prev, [axis]: !prev[axis] }));
    };

    const chartData = {
        labels: scores.map(s => s.week),
        datasets: MOCK_AXES.filter(axis => visibleAxes[axis]).map(axis => ({
            label: axis.charAt(0).toUpperCase() + axis.slice(1),
            data: scores.map(s => s.scores[axis]),
            borderColor: MOCK_COLORS[axis as keyof typeof MOCK_COLORS],
            backgroundColor: MOCK_COLORS[axis as keyof typeof MOCK_COLORS].replace('1)', '0.2)'),
            fill: false,
            tension: 0.1
        })),
    };

    if (isLoading) return <div className="text-center p-4">Loading radar trends...</div>;
    if (error) return <div className="text-center p-4 text-red-500">{error}</div>;

    return (
        <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Radar Trend Over Time</h2>
            <div className="mb-4 flex flex-wrap gap-x-4 gap-y-2">
                {MOCK_AXES.map(axis => (
                    <label key={axis} className="inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={visibleAxes[axis]}
                            onChange={() => handleToggleAxis(axis)}
                            className="form-checkbox h-5 w-5 rounded"
                            style={{ accentColor: MOCK_COLORS[axis as keyof typeof MOCK_COLORS] }}
                        />
                        <span className="ml-2 text-sm text-gray-700">{axis.charAt(0).toUpperCase() + axis.slice(1)}</span>
                    </label>
                ))}
            </div>
            <Line data={chartData} />
        </div>
    );
};

export default RadarTrendGraph; 